import { App } from '@capacitor/app'
import { Capacitor, CapacitorHttp } from '@capacitor/core'
import { Dialog } from '@capacitor/dialog'
import { SplashScreen } from '@capacitor/splash-screen'
import { type BundleInfo, CapacitorUpdater } from '@capgo/capacitor-updater'
import { compareVersions, parseVersion } from '@faceup/utils'
import { version } from '../../package.json'

// we need to get fresh version for `OTA` update - https://github.com/ionic-team/capacitor/pull/2146#issuecomment-791392573
// the domain has to be different from the one in capacitor.config.ts, but we have to allow it for CORS
// but becase we do not have nntb.cz domain anymore, we are using the same domain, but the requests has to be made via `CapacitorHttp` (not via fetch)
const APP_URL = `https://www.app.faceup.${import.meta.env.VITE_ENVIRONMENT === 'production' ? 'com' : 'dev'}`

// global variable to be able to mutate it
let newVersion: BundleInfo = {
  id: '',
  version: '',
  downloaded: '',
  checksum: '',
  status: 'success',
}

const updateAppCheck = async (isActive: boolean) => {
  if (!isActive) {
    return
  }

  try {
    // because we are making call to the same domain as the app is served, we have to use CapacitorHttp (not fetch)
    const response = await CapacitorHttp.get({
      url: `${APP_URL}/version.json`,
      params: { date: String(Date.now()) },
      responseType: 'json',
    })

    const {
      data: { version: latestVersion },
    } = response

    const { bundle: currentBundle } = await CapacitorUpdater.current()
    const currentVersion = currentBundle.id === 'builtin' ? version : currentBundle.version

    if (shouldUpdate(latestVersion, currentVersion) && newVersion.version !== latestVersion) {
      newVersion = await CapacitorUpdater.download({
        url: `${APP_URL}/build.zip`,
        version: latestVersion,
      })

      // set app new version; in the ideal world, we would like to set it in condition `!isActive && newVersion.version`
      SplashScreen.show()
      try {
        await CapacitorUpdater.set(newVersion)
        await deleteUnusedVersions(newVersion.version)
      } catch {
        // ignore empty catch
      } finally {
        SplashScreen.hide()
      }
    }
  } catch {
    // ignore empty catch
  }
}

// Over The Air Update
export const OTAUpdate = async () => {
  // Update replaces the native webview bundle, so it only applies to the real
  // app. isPurePWA() would also match a browser (user agent, display mode).
  if (!Capacitor.isNativePlatform()) {
    return
  }

  CapacitorUpdater.notifyAppReady()
  // fire update check manually because appStateChange is not fired on app start, but only after state change
  await updateAppCheck(true)
  App.addListener('appStateChange', ({ isActive }) => updateAppCheck(isActive))
}

const deleteUnusedVersions = async (excludeBuildVersion: string) => {
  const { bundles } = await CapacitorUpdater.list()

  await Promise.all(
    bundles
      .filter(bundle => excludeBuildVersion !== bundle.version)
      .map(bundle => CapacitorUpdater.delete({ id: bundle.id }))
  )
}

const showUpdateDialog = async (): Promise<void> => {
  await Dialog.confirm({
    title: 'Update Available',
    message: 'Please update to the latest version of the app',
  })

  return showUpdateDialog()
}

/**
 * Determines if an OTA update should be performed
 * @param latest - Latest version available on the server
 * @param current - Current app version
 * @returns true if update should be performed, false otherwise
 *
 * Major version changes require App Store/Play Store update
 */
const shouldUpdate = (latest: string, current: string) => {
  try {
    const latestVersion = parseVersion(latest)
    const currentVersion = parseVersion(current)

    const comparison = compareVersions(latestVersion, currentVersion)

    // latest contains new binaries -> major version change requires store update
    if (latestVersion.major > currentVersion.major) {
      showUpdateDialog()
      return false
    }

    // Update is available if latest > current
    return comparison > 0
  } catch (error) {
    console.error('Error parsing versions:', error)
    return false
  }
}
